import SleepDiaryChart, {
  LegendItem,
} from "@/components/report/commons/SleepDiaryChart";
import { TaskAlt } from "@mui/icons-material";
import { Box, Button, Dialog, Divider, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { ChartData } from "./SleepJudge/DeviceMeasurementData";

/**
 * {@link BeforeCheckReportDialog} のプロパティ
 */
export interface BeforeCheckReportDialogProps {
  /** ダイアログを開く */
  open: boolean;
  /** ダイアログを閉じる */
  close: () => void;
  /** 測定データ */
  chart_data: ChartData[];
  /** 受診勧奨オプション */
  useRecommend?: boolean;
}
const MESSAGES = [
  "診断ガイドラインに基づき、睡眠時無呼吸症候群・不眠症のリスクを表示していますが、病気の診断や治療を意図するものではありません。結果に関わらず、気になることがある場合には、かかりつけ医・睡眠の専門医に相談することをおすすめします。",
] as const;
/**
 * レポートを確認する前にダイアログ
 * @param param0 {@link BeforeCheckReportDialogProps}
 * @returns レポートを確認する前にダイアログ
 */
export function BeforeCheckReportDialog({
  open,
  close,
  chart_data,
  useRecommend,
}: BeforeCheckReportDialogProps) {
  const [counter, setCounter] = useState(0);
  return (
    <Dialog open={open} sx={{ borderRadius: "16px" }}>
      {
        [
          <BeforeCheckReport
            next={() =>
              setCounter((c) => {
                return c + 1;
              })
            }
            useRecommend={useRecommend}
          />,
          <YourChart next={() => close()} chart_data={chart_data} />,
        ][counter]
      }
    </Dialog>
  );
}

function YourChart({
  next,
  chart_data,
}: {
  next: () => void;
  chart_data: ChartData[];
}) {
  return (
    <Stack sx={{ p: 4 }}>
      <Typography title="subtitle1" color="primary.main" sx={{ mb: 4 }}>
        あなたの測定データ
      </Typography>

      <Divider />
      <Stack sx={{ gap: 4, my: 2 }}>
        <Stack direction="row" justifyContent="end" sx={{ gap: 4 }}>
          <LegendItem
            imageName="image_report_how_to_view_graphs_light_off.svg"
            text="消灯"
          />
          <LegendItem
            imageName="image_report_how_to_view_graphs_wake_up.svg"
            text="起床"
          />
          <LegendItem
            imageName="image_report_how_to_view_graphs_sleep.svg"
            text="睡眠"
          />
          <LegendItem
            imageName="image_report_how_to_view_graphs_mid-awakening.svg"
            text="睡眠中の覚醒"
          />
        </Stack>
        <Stack sx={{ height: "300px" }}>
          <SleepDiaryChart
            data={chart_data.map((d) => ({
              date: d.date,
              bedtime: d.bedtime == null ? null : new Date(d.bedtime),
              ariseTime: d.arise_time == null ? null : new Date(d.arise_time),
              sleepData: d.sleep_data.map((d) => ({
                from: new Date(d.from),
                to: new Date(d.to),
                type: d.type,
              })),
            }))}
          />
        </Stack>

        <Box textAlign="center">
          <Button
            variant="contained"
            size="small"
            sx={{ borderRadius: 4, px: 8 }}
            onClick={next}
          >
            レポートを見る
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}

function BeforeCheckReport({
  next,
  useRecommend,
}: {
  next: () => void;
  useRecommend?: boolean;
}) {
  return (
    <Stack sx={{ p: 4 }}>
      <Typography title="subtitle1" color="error.main" sx={{ mb: 4 }}>
        レポートを確認する前に
      </Typography>

      <Divider />

      <Stack sx={{ py: 4, gap: 4 }}>
        {useRecommend &&
          MESSAGES.map((m, i) => (
            <Stack key={i} direction="row">
              <TaskAlt fontSize="small" color="error" sx={{ pr: 2, pt: 1 }} />
              <Typography variant="body2">{m}</Typography>
            </Stack>
          ))}
        <Stack sx={{ p: 4, backgroundColor: "#ebf2f8" }}>
          <Typography fontWeight={500} fontSize="14px">
            まずは、次のページで今回あなたが測定した睡眠データについて確認してから、レポートの確認に進みましょう。
          </Typography>
        </Stack>
      </Stack>
      <Box textAlign="center">
        <Button
          variant="outlined"
          size="small"
          sx={{ borderRadius: "20px", px: 8 }}
          onClick={next}
        >
          つぎへ
        </Button>
      </Box>
    </Stack>
  );
}
